import { mapInstance } from './propertySingleGmap';

class PropertySpotlight {
    constructor() {
        this.mainSpotlight = document.getElementById('c-img_spotlight_cont');
        this.spotlightItems = document.querySelectorAll('.c-spotlight_item');
        this.imageCache = new Map();
        this.isAnimating = false;
        this.currentTimeline = null;
        this.mapContainer = document.querySelector('.c-google_map_cont.cc-hero');
        this.mapOverlay = document.getElementById('e-map_hero_overlay');
        
        // Set initial index based on the first visible image
        const initialImage = this.mainSpotlight.querySelector('img:not(.spotlight-transition)');
        
        if (initialImage) {
            const initialSrc = initialImage.src;
            let found = false;
            this.spotlightItems.forEach(item => {
                if (item.dataset.fullsize === initialSrc) {
                    this.currentIndex = parseInt(item.dataset.index);
                    found = true;
                }
            });
            if (!found) {
                // If no match found, get the first non-map item's data-index
                const firstNonMapItem = Array.from(this.spotlightItems).find(item => !item.classList.contains('cc-map_hero'));
                this.currentIndex = firstNonMapItem ? parseInt(firstNonMapItem.dataset.index) : 0;
            }
        } else {
            // If no initial image, get the first non-map item's data-index
            const firstNonMapItem = Array.from(this.spotlightItems).find(item => !item.classList.contains('cc-map_hero'));
            this.currentIndex = firstNonMapItem ? parseInt(firstNonMapItem.dataset.index) : 0;
        }
        
        this.init();
    }

    init() {
        if (!this.mainSpotlight || !this.spotlightItems.length) return;
        
        // Initialize navigation buttons
        const prevButton = this.mainSpotlight.querySelector('.c-arrow_btn_prev');
        const nextButton = this.mainSpotlight.querySelector('.c-arrow_btn_next');
        
        if (prevButton) {
            prevButton.addEventListener('click', () => this.navigateSpotlight('prev'));
        }
        
        if (nextButton) {
            nextButton.addEventListener('click', () => this.navigateSpotlight('next'));
        }
        
        this.spotlightItems.forEach(item => {
            // Handle click events
            if (item.classList.contains('cc-map_hero')) {
                // For map item, use the overlay button
                const overlay = item.querySelector('.e-map_hero_overlay');
                if (overlay) {
                    overlay.addEventListener('click', () => this.showMap());
                }
                // Also make the entire map item clickable as fallback
                item.addEventListener('click', () => this.showMap());
            } else {
                item.addEventListener('click', () => this.updateSpotlight(item));
            }
            
            // Handle hover for preloading (only for image items)
            if (!item.classList.contains('cc-map_hero')) {
                item.addEventListener('mouseenter', () => this.preloadImage(item));
            }
            
            // Accessibility
            item.setAttribute('role', 'button');
            item.setAttribute('tabindex', '0');
            item.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (item.classList.contains('cc-map_hero')) {
                        this.showMap();
                    } else {
                        this.updateSpotlight(item);
                    }
                }
            });
        });
    }

    navigateSpotlight(direction) {
        if (this.isAnimating) return;
        
        // Get total items excluding the map
        const nonMapItems = Array.from(this.spotlightItems).filter(item => !item.classList.contains('cc-map_hero'));
        const totalItems = nonMapItems.length;
        
        // Find the current item's data-index
        const currentItem = Array.from(this.spotlightItems).find(item => 
            parseInt(item.dataset.index) === this.currentIndex
        );
        
        // Get all data-index values
        const indices = nonMapItems.map(item => parseInt(item.dataset.index)).sort((a, b) => a - b);
        const currentIndexPosition = indices.indexOf(this.currentIndex);
        
        let newIndex;
        if (direction === 'next') {
            newIndex = indices[(currentIndexPosition + 1) % indices.length];
        } else {
            newIndex = indices[(currentIndexPosition - 1 + indices.length) % indices.length];
        }
        
        // Find the item with the matching data-index
        const nextItem = Array.from(this.spotlightItems).find(item => 
            parseInt(item.dataset.index) === newIndex
        );
        
        if (nextItem) {
            this.currentIndex = newIndex;
            this.updateSpotlight(nextItem);
        }
    }

    preloadImage(item) {
        const fullSizeUrl = item.dataset.fullsize;
        if (!fullSizeUrl || this.imageCache.has(fullSizeUrl)) return;

        const img = new Image();
        img.src = fullSizeUrl;
        img.onload = () => {
            this.imageCache.set(fullSizeUrl, img);
        };
    }

    showMap() {
        if (this.currentTimeline) {
            this.currentTimeline.kill();
            
            // Clean up any transitioning images
            const transitioning = this.mainSpotlight.querySelectorAll('.spotlight-transition');
            transitioning.forEach(img => img.remove());
        }

        // Reset current index to 0 when showing map
        this.currentIndex = -1;

        // Create map container clone
        const mapClone = this.mapContainer.cloneNode(true);
        mapClone.classList.add('spotlight-transition');
        mapClone.style.opacity = '0';
        mapClone.style.position = 'absolute';
        mapClone.style.top = '0';
        mapClone.style.left = '0';
        mapClone.style.width = '100%';
        mapClone.style.height = '100%';

        // Generate new unique ID for the map container
        const newMapId = 'property-single-map-spotlight';
        mapClone.querySelector('#property-single-map').id = newMapId;

        // Add map to container
        const currentImage = this.mainSpotlight.querySelector('img:not(.spotlight-transition)');
        this.mainSpotlight.appendChild(mapClone);

        // Create and store the new timeline
        this.currentTimeline = gsap.timeline({
            onComplete: () => {
                if (currentImage) {
                    currentImage.remove();
                }
                mapClone.style.position = 'relative';
                mapClone.classList.remove('spotlight-transition');
                this.currentTimeline = null;
            }
        });

        // Initialize new map instance after a short delay
        setTimeout(() => {
            mapInstance.renderMap(newMapId);
        }, 50);

        // Fade out current image (if it exists)
        if (currentImage) {
            this.currentTimeline.to(currentImage, {
                opacity: 0,
                duration: 0.5,
                ease: "power2.out"
            });
        }

        // Fade in map
        this.currentTimeline.to(mapClone, {
            opacity: 1,
            duration: 0.7,
            ease: "power2.Out"
        });

        return this.currentTimeline;
    }

    async updateSpotlight(clickedItem) {
        // Update current index using data-index attribute
        this.currentIndex = parseInt(clickedItem.dataset.index);

        // Remove any existing map in the spotlight
        const existingMap = this.mainSpotlight.querySelector('.c-google_map_cont');
        if (existingMap) {
            existingMap.remove();
        }

        // If there's a current animation, kill it and clean up
        if (this.currentTimeline) {
            this.currentTimeline.kill();
            
            // Clean up any transitioning images
            const transitioning = this.mainSpotlight.querySelectorAll('.spotlight-transition');
            transitioning.forEach(img => img.remove());
            
            // Keep only the most recently successful image
            const images = this.mainSpotlight.querySelectorAll('img');
            for (let i = 0; i < images.length - 1; i++) {
                images[i].remove();
            }
        }

        const fullSizeUrl = clickedItem.dataset.fullsize;
        if (!fullSizeUrl) return;

        // Create new image element
        const newImage = document.createElement('img');
        newImage.className = 'img-fluid spotlight-transition';
        newImage.src = fullSizeUrl;
        const imageNumber = parseInt(clickedItem.dataset.index) + 1;
        newImage.setAttribute('aria-label', `Property image ${imageNumber} of ${this.spotlightItems.length}`);

        // Set initial styles
        newImage.style.opacity = '0';
        newImage.style.position = 'absolute';
        newImage.style.top = '0';
        newImage.style.left = '0';
        newImage.style.width = '100%';
        newImage.style.height = '100%';
        newImage.style.objectFit = 'cover';
        
        // Ensure container has relative positioning
        if (getComputedStyle(this.mainSpotlight).position === 'static') {
            this.mainSpotlight.style.position = 'relative';
        }

        // Add new image to container
        const currentImage = this.mainSpotlight.querySelector('img:not(.spotlight-transition)');
        this.mainSpotlight.appendChild(newImage);

        // Create and store the new timeline
        this.currentTimeline = gsap.timeline({
            onComplete: () => {
                if (currentImage) {
                    currentImage.remove();
                }
                // Reset positioning on new image after animation
                newImage.style.position = 'relative';
                newImage.classList.remove('spotlight-transition');
                this.currentTimeline = null;
            }
        });

        gsap.set(newImage, {
            scale: 1.02
        });

        // Fade in new image
        this.currentTimeline.to(newImage, {
            opacity: 1,
            scale: 1,
            duration: .7,
            ease: "power2.Out"
        });

        // Return the timeline in case we need to handle it externally
        return this.currentTimeline;
    }

    // Optional: Method to manually cancel current animation
    cancelCurrentAnimation() {
        if (this.currentTimeline) {
            this.currentTimeline.kill();
            this.currentTimeline = null;
        }
    }
}

export default PropertySpotlight; 